import { useSelector } from 'react-redux';

import { type ServiceApiName } from '../config/configuration.type';
import store from '../../store/store';
import { type HttpMethod, throwHttpErrors } from './restApiUtils';
import HttpError from './HttpError';
import { selectAppLanguage } from '../../store/selectors/i18n.selectors';
import { type Locale } from '../../store/i18nSlice';

const methodSet = new Set(['PATCH', 'POST', 'PUT']);

export const buildUrl = (serviceName: ServiceApiName, resourcePath: string) => {
  const config = store.getState().configuration.config;
  return `${config.services[serviceName].url}/${resourcePath}`;
};

const generateIcareHeader = ({ fullLocale, appLanguage }: { fullLocale?: boolean; appLanguage: Locale | null }) => {
  const headers = new Headers();

  if (appLanguage) {
    headers.append(
      'accept-language',
      fullLocale ? `${appLanguage.language.toLowerCase()}-${appLanguage.country.toUpperCase()}` : appLanguage.language
    );
  }

  return headers;
};

export const buildHeaders = (fullLocale?: boolean): Headers => {
  const appLanguage = selectAppLanguage(store.getState());
  return generateIcareHeader({ fullLocale, appLanguage });
};

export const useBuildHeader = ({ fullLocale }: { fullLocale?: boolean } = {}): Record<string, string> => {
  const appLanguage = useSelector(selectAppLanguage);

  return Object.fromEntries(generateIcareHeader({ fullLocale, appLanguage }));
};

export const buildRequest = (url: string, method: HttpMethod, headers: Headers, body?: string | FormData, fileUpload?: boolean): Request => {
  const config = store.getState().configuration.config;

  if (methodSet.has(method)) {
    !fileUpload && headers.append('Content-Type', 'application/json');
  }

  const requestInit: RequestInit = {
    method,
    headers,
    body,
    credentials: url.includes(config.services.ICARE_BACK.url) ? 'include' : 'same-origin',
  };

  return new Request(url, requestInit);
};

export const fetchRequest = async (request: Request): Promise<Response> => {
  try {
    const response = await fetch(request);

    return throwHttpErrors(response);
  } catch (initialError) {
    if (!(initialError instanceof HttpError) || initialError.response?.status !== 401) {
      throw initialError;
    }

    const { authUrl } = await initialError.response.json();

    if (authUrl) {
      globalThis.location.replace(authUrl);
    }

    throw initialError;
  }
};

export const fetchForRedux = async (requestInfo: RequestInfo | URL, requestInit: RequestInit | undefined) => {
  const response = await fetchRequest(new Request(requestInfo, requestInit));
  const contentType = response.headers.get('content-type');
  if (contentType?.includes('application/json')) {
    return response.json();
  }
  return response.text();
};
